import { createContext, type ReactNode, useContext } from 'react';

import type { Services } from './types';

/**
 * The only React file in the services layer (ADR-0003, ADR-0015).
 *
 * ONE context for four services, not four contexts. The reason four would be tempting is
 * render isolation, and there is none to win: a `Services` object is built once in
 * `createServices()` and never replaced, so the provider's value is referentially stable for
 * the life of the application and no consumer re-renders because of it.
 *
 * `useServices()` IS a service locator, and ADR-0015 says so in its own consequences
 * section: a component that forgets the provider fails at runtime, and a dependency taken
 * through a hook is invisible in every signature. The containment is that only COMPONENTS
 * may use it. Commands receive `ctx` — which holds `services` — as an explicit argument, so
 * the logic layer stays runnable in bare Node (ADR-0014).
 */

const ServicesContext = createContext<Services | null>(null);

export interface ServicesProviderProps {
  readonly services: Services;
  readonly children: ReactNode;
}

export function ServicesProvider({ services, children }: ServicesProviderProps): ReactNode {
  return <ServicesContext.Provider value={services}>{children}</ServicesContext.Provider>;
}

/**
 * The four services, or a thrown error naming the fix.
 *
 * A `null` default with a throw rather than a silently-working fallback object: a missing
 * provider that quietly hands back no-op services produces an application where Copy does
 * nothing and nothing says why. Failing at the first hook call, in development, with the
 * component's own stack, is the cheaper failure.
 */
export function useServices(): Services {
  const services = useContext(ServicesContext);
  if (services === null) {
    throw new Error('useServices() requires a <ServicesProvider services={…}> above it.');
  }
  return services;
}
